import { useState, useEffect } from 'react';
import { createEvent } from '../../../api/api';
import LocationPicker from './LocationPicker';
import AddressAutocomplete from './AddressAutocomplete';
import './CreateEvent.css';

const EMPTY_GUEST = { name: '', email: '', phone: '', address: '' };

const LOCATION_DATA = {
  India: {
    'Andhra Pradesh': ['Amaravati', 'Guntur', 'Hyderabad', 'Vijayawada', 'Visakhapatnam'],
    'Arunachal Pradesh': ['Itanagar', 'Pasighat', 'Tawang'],
    Assam: ['Guwahati', 'Jorhat', 'Silchar'],
    Bihar: ['Bhagalpur', 'Gaya', 'Patna'],
    Chhattisgarh: ['Bilaspur', 'Dhamtari', 'Raipur'],
    'Goa': ['Mapusa', 'Panaji', 'Ponda'],
    Gujarat: ['Ahmedabad', 'Gandhinagar', 'Rajkot', 'Surat', 'Vadodara'],
    Haryana: ['Ambala', 'Faridabad', 'Gurugram', 'Hisar', 'Karnal'],
    'Himachal Pradesh': ['Kullu', 'Manali', 'Shimla'],
    'Jammu & Kashmir': ['Jammu', 'Srinagar'],
    Jharkhand: ['Dhanbad', 'Jamshedpur', 'Ranchi'],
    Karnataka: ['Bangalore', 'Hubli', 'Mysore'],
    Kerala: ['Calicut', 'Kannur', 'Kochi', 'Thiruvananthapuram'],
    'Madhya Pradesh': ['Bhopal', 'Gwalior', 'Indore', 'Jabalpur'],
    Maharashtra: ['Aurangabad', 'Mumbai', 'Nagpur', 'Pune'],
    Manipur: ['Imphal'],
    Meghalaya: ['Shillong'],
    Mizoram: ['Aizawl'],
    Nagaland: ['Kohima', 'Mokokchung'],
    Odisha: ['Bhubaneswar', 'Cuttack', 'Puri'],
    Punjab: ['Amritsar', 'Jalandhar', 'Ludhiana', 'Patiala'],
    Rajasthan: ['Jaipur', 'Jodhpur', 'Udaipur'],
    Sikkim: ['Gangtok'],
    'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Salem'],
    Telangana: ['Hyderabad', 'Nizamabad', 'Warangal'],
    Tripura: ['Agartala'],
    'Uttar Pradesh': ['Agra', 'Allahabad', 'Kanpur', 'Lucknow', 'Varanasi'],
    Uttarakhand: ['Dehradun', 'Haridwar', 'Rishikesh'],
    'West Bengal': ['Howrah', 'Kolkata', 'Siliguri'],
    Delhi: ['New Delhi'],
  },
};

function CreateEvent({ onCancel, onSuccess }) {
  const [title, setTitle] = useState('');
  const [eventType, setEventType] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [message, setMessage] = useState('');
  const [guests, setGuests] = useState([EMPTY_GUEST]);
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [locationName, setLocationName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [locationError, setLocationError] = useState('');
  const [showMapPicker, setShowMapPicker] = useState(false);

  // Load template data from localStorage if available
  useEffect(() => {
    const templateData = localStorage.getItem('selectedTemplate');
    if (templateData) {
      try {
        const template = JSON.parse(templateData);
        setTitle(template.title || '');
        setMessage(template.description || '');
        setEventType(template.category || '');
        localStorage.removeItem('selectedTemplate'); // Clear after using
      } catch (err) {
        console.error('Failed to load template data:', err);
      }
    }
  }, []);

  const states = country ? Object.keys(LOCATION_DATA[country] || {}).sort() : [];
  const cities = state && country ? (LOCATION_DATA[country]?.[state] || []) : [];

  const addGuest = () => setGuests((prev) => [...prev, EMPTY_GUEST]);

  const updateGuest = (index, field, value) => {
    setGuests((prev) =>
      prev.map((g, i) => (i === index ? { ...g, [field]: value } : g))
    );
  };

  const handleLocationSelect = (locationData) => {
    setCountry(locationData.country);
    setState(locationData.state);
    setCity(locationData.city);
    setLocationName(locationData.locationName);
    setShowMapPicker(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Check for location validation error
    if (locationError) {
      setError('Please fix the location error before submitting.');
      setLoading(false);
      return;
    }

    try {
      const validGuests = guests.filter(g => g.name && g.email);

      const eventData = {
        title,
        eventType,
        imageUrl: imageUrl || undefined,
        eventDate,
        eventTime,
        country,
        state,
        city,
        locationName,
        message,
        guests: validGuests.map(g => ({ name: g.name, email: g.email, phone: g.phone || undefined, address: g.address || undefined })),
      };

      await createEvent(eventData);
      if (onSuccess) onSuccess();
      onCancel();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="create-event">
      <div className="create-event-header">
        <h1 className="create-event-title">Create Event</h1>
        <p className="create-event-subtitle">Fill in the details for your event</p>
      </div>

      <form className="create-event-form" onSubmit={handleSubmit}>
        {/* Event Details card */}
        <div className="create-event-card">
          <h2 className="create-event-section-title">Event Details</h2>

          <div className="create-event-field">
            <label>Event Title *</label>
            <input type="text" placeholder="e.g., Raj's Birthday Party" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>

          <div className="create-event-field">
            <label>Event Type *</label>
            <input type="text" placeholder="Birthday, Wedding, etc." value={eventType} onChange={(e) => setEventType(e.target.value)} required />
          </div>

          <div className="create-event-field">
            <label className="create-event-label-row">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="1" y="3" width="14" height="11" rx="1.5" stroke="#6b7280" strokeWidth="1.5" />
                <path d="M1 7h14" stroke="#6b7280" strokeWidth="1.5" />
              </svg>
              Event Image URL (optional)
            </label>
            <input type="text" placeholder="https://example.com/image.jpg" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} />
            <span className="create-event-hint">Leave blank to use default image for event type</span>
          </div>

          <div className="create-event-row">
            <div className="create-event-field">
              <label className="create-event-label-row">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="6.5" stroke="#6b7280" strokeWidth="1.5" />
                  <path d="M8 4v4l3 2" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                Date *
              </label>
              <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} required />
            </div>
            <div className="create-event-field">
              <label className="create-event-label-row">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="6.5" stroke="#6b7280" strokeWidth="1.5" />
                  <path d="M8 4v4l3 2" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                Time *
              </label>
              <input type="time" value={eventTime} onChange={(e) => setEventTime(e.target.value)} required />
            </div>
          </div>

          <div className="create-event-location-grid">
            {/* Country */}
            <div className="create-event-field">
              <label className="create-event-label-row">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="6.5" stroke="#6b7280" strokeWidth="1.5" />
                  <ellipse cx="8" cy="8" rx="3" ry="6.5" stroke="#6b7280" strokeWidth="1.5" />
                  <path d="M1.5 8h13" stroke="#6b7280" strokeWidth="1.5" />
                </svg>
                Country *
              </label>
              <div className="create-event-select-wrap">
                <select
                  value={country}
                  onChange={(e) => { setCountry(e.target.value); setState(''); setCity(''); }}
                  required
                >
                  <option value="">Select Country</option>
                  {Object.keys(LOCATION_DATA).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <svg className="create-event-select-arrow" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M3 4.5l3 3 3-3" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>

            {/* State */}
            <div className="create-event-field">
              <label className="create-event-label-row">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="2" y="4" width="12" height="9" rx="1.5" stroke="#6b7280" strokeWidth="1.5" />
                  <path d="M2 7h12" stroke="#6b7280" strokeWidth="1.5" />
                  <path d="M5 1.5h6" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                State *
              </label>
              <div className="create-event-select-wrap">
                <select
                  value={state}
                  disabled={!country}
                  onChange={(e) => { setState(e.target.value); setCity(''); }}
                  required
                >
                  <option value="">Select State</option>
                  {states.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <svg className="create-event-select-arrow" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M3 4.5l3 3 3-3" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>

            {/* City */}
            <div className="create-event-field">
              <label className="create-event-label-row">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="2" y="4" width="12" height="9" rx="1.5" stroke="#6b7280" strokeWidth="1.5" />
                  <path d="M2 7h12" stroke="#6b7280" strokeWidth="1.5" />
                  <path d="M5 1.5h6" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                City *
              </label>
              <div className="create-event-select-wrap">
                <select
                  value={city}
                  disabled={!state}
                  onChange={(e) => setCity(e.target.value)}
                  required
                >
                  <option value="">Select City</option>
                  {cities.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <svg className="create-event-select-arrow" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M3 4.5l3 3 3-3" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>

            {/* Location Name */}
            <div className="create-event-field">
              <label className="create-event-label-row">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 1.5C5.51 1.5 3.5 3.51 3.5 6c0 3.5 4.5 8.5 4.5 8.5s4.5-5 4.5-8.5c0-2.49-2.01-4.5-4.5-4.5z" stroke="#6b7280" strokeWidth="1.5" strokeLinejoin="round" />
                  <circle cx="8" cy="6" r="1.5" stroke="#6b7280" strokeWidth="1.5" />
                </svg>
                Location Name *
              </label>
              <AddressAutocomplete
                placeholder="Enter venue address"
                value={locationName}
                restrictCountry={country}
                restrictState={state}
                restrictCity={city}
                onChange={(addressData) => {
                  setLocationError(''); // Clear error on change

                  if (typeof addressData === 'object' && addressData.fullAddress) {
                    // Address selected from autocomplete - auto-fill all fields
                    setLocationName(addressData.fullAddress);
                    if (addressData.country) setCountry(addressData.country);
                    if (addressData.state) setState(addressData.state);
                    if (addressData.city) setCity(addressData.city);
                  } else {
                    // Manual typing
                    setLocationName(addressData);
                  }
                }}
                required
              />
              {locationError && (
                <span className="create-event-hint" style={{ color: '#dc2626', fontWeight: 500 }}>
                  {locationError}
                </span>
              )}
            </div>

            {/* Map Picker Button */}
            <div className="create-event-field" style={{ gridColumn: '1 / -1' }}>
              <button
                type="button"
                className="create-event-map-button"
                onClick={() => setShowMapPicker(true)}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 1.5C5.51 1.5 3.5 3.51 3.5 6c0 3.5 4.5 8.5 4.5 8.5s4.5-5 4.5-8.5c0-2.49-2.01-4.5-4.5-4.5z" stroke="#6C63FF" strokeWidth="1.5" strokeLinejoin="round" />
                  <circle cx="8" cy="6" r="1.5" stroke="#6C63FF" strokeWidth="1.5" />
                </svg>
                Or Pick Location from Map
              </button>
            </div>
          </div>

          <div className="create-event-field">
            <label className="create-event-label-row">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="1" y="3" width="14" height="10" rx="1.5" stroke="#6b7280" strokeWidth="1.5" />
                <path d="M1 6h14" stroke="#6b7280" strokeWidth="1.5" />
              </svg>
              Message *
            </label>
            <textarea rows="4" placeholder="Write a message for your guests..." value={message} onChange={(e) => setMessage(e.target.value)} required />
          </div>
        </div>

        {/* Guest List card */}
        <div className="create-event-card">
          <h2 className="create-event-section-title">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="6" cy="6" r="3" stroke="#1a1a2e" strokeWidth="1.5" />
              <path d="M1 16c0-2.76 2.24-5 5-5s5 2.24 5 5" stroke="#1a1a2e" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="13" cy="7" r="2" stroke="#1a1a2e" strokeWidth="1.5" />
              <path d="M13 11c1.66 0 3 1.34 3 3" stroke="#1a1a2e" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            Guest List
          </h2>

          {guests.map((guest, i) => (
            <div key={i} className="create-event-guest-row">
              <input
                type="text"
                placeholder="Name"
                value={guest.name}
                onChange={(e) => updateGuest(i, 'name', e.target.value)}
              />
              <input
                type="email"
                placeholder="Email"
                value={guest.email}
                onChange={(e) => updateGuest(i, 'email', e.target.value)}
              />
              <input
                type="tel"
                placeholder="WhatsApp Number"
                value={guest.phone}
                onChange={(e) => updateGuest(i, 'phone', e.target.value)}
              />
              {/* <input
                type="text"
                placeholder="Address (optional)"
                value={guest.address}
                onChange={(e) => updateGuest(i, 'address', e.target.value)}
              /> */}
            </div>
          ))}

          <button type="button" className="create-event-add-guest" onClick={addGuest}>
            <span>+</span> Add Guest
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="create-event-error">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6.5" stroke="#dc2626" strokeWidth="1.5" />
              <path d="M8 4v4.5" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="8" cy="11" r="0.5" fill="#dc2626" />
            </svg>
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="create-event-actions">
          <button type="button" className="create-event-cancel" onClick={onCancel} disabled={loading}>Cancel</button>
          <button type="submit" className="create-event-submit" disabled={loading}>
            {loading ? 'Creating...' : 'Create Event'}
          </button>
        </div>
      </form>

      {/* Map Picker Modal */}
      {showMapPicker && (
        <div className="create-event-modal-overlay" onClick={() => setShowMapPicker(false)}>
          <div className="create-event-modal" onClick={(e) => e.stopPropagation()}>
            <div className="create-event-modal-header">
              <h3>Select Venue Location</h3>
              <button
                type="button"
                className="create-event-modal-close"
                onClick={() => setShowMapPicker(false)}
              >
                ×
              </button>
            </div>
            <LocationPicker onLocationSelect={handleLocationSelect} />
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateEvent;
